import { useMemo } from 'react';
import { useBrandAnalysisData } from '../contexts';

const ENV_PRODUCTION = 'production';
const ENV_DEVELOPMENT = 'development';
const ENV_STAGING = 'staging';

const HOSTNAMES = {
  development: ['localhost', 'my.yoast.test'],
  staging: ['staging', 'pages.dev'],
  production: ['my.yoast.com'],
} as const;

/**
 * Custom hook to get environment flags from hostAppProps.env
 * Returns isProduction, isDevelopment, isStaging booleans.
 * Uses hostname matching with production as fallback/safety.
 * Memoized to prevent unnecessary recalculations on re-renders.
 */
export function useEnv() {
  const { hostAppProps } = useBrandAnalysisData();
  const env = hostAppProps?.env;

  const hostname = useMemo(
    () => (typeof window !== 'undefined' ? window.location.hostname : ''),
    [],
  );

  return useMemo(() => {
    const matchesHostname = (patterns: readonly string[]) =>
      patterns.some((pattern) => hostname.includes(pattern));

    const isDevelopment = env
      ? env === ENV_DEVELOPMENT
      : // Determine environment based on hostname patterns if env is not provided
        matchesHostname(HOSTNAMES.development);

    const isStaging = env
      ? env === ENV_STAGING
      : matchesHostname(HOSTNAMES.staging);

    const isProduction = env
      ? env === ENV_PRODUCTION
      : // Production is fallback - if not dev or staging, assume production
        !isDevelopment && !isStaging;

    return {
      env:
        env ||
        (isDevelopment
          ? ENV_DEVELOPMENT
          : isStaging
            ? ENV_STAGING
            : ENV_PRODUCTION),
      isProduction,
      isDevelopment,
      isStaging,
    };
  }, [env, hostname]);
}
